import { useEffect, useState } from "react";

import { reactivateAccount, sendReactivationCode } from "../api/account";

// Answers after which only signing in again helps: the 15 minutes since
// sign-in ran out, or the account is no longer one that can come back.
const START_OVER = new Set([
  "REACTIVATION_EXPIRED",
  "ALREADY_ACTIVE",
  "REACTIVATION_UNAVAILABLE",
]);

const INPUT_CLASS =
  "h-[52px] w-full rounded-xl border border-[var(--color-border)] bg-transparent px-4 text-sm tracking-[0.3em] text-[var(--color-text)] placeholder:tracking-normal placeholder:text-[var(--color-muted)] transition focus:border-[var(--color-accent)]";
// The sign-in button's own look, so the card keeps one primary button.
const PRIMARY_CLASS =
  "flex h-[52px] w-full cursor-pointer items-center justify-center rounded-xl bg-[var(--color-accent)] text-sm font-bold text-[var(--color-primary-contrast)] shadow-[0_8px_18px_rgba(13,27,61,0.20)] transition hover:-translate-y-0.5 disabled:cursor-default disabled:opacity-60 disabled:hover:translate-y-0 sm:h-14";
const LINK_CLASS =
  "text-sm font-semibold text-[var(--color-primary)] transition hover:text-[var(--color-accent)] disabled:cursor-default disabled:text-[var(--color-muted)]";

function formatDay(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

/**
 * Shown on the sign-in page when the account was deleted by its owner. A
 * code emailed to the account brings it back as it was; `onReactivated`
 * receives the new session. `onCancel` returns to the sign-in form.
 */
export function ReactivateAccountPanel({
  token,
  email,
  deletedAt,
  onReactivated,
  onCancel,
}) {
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ended, setEnded] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  function fail(err) {
    if (START_OVER.has(err?.code)) setEnded(err.message);
    else setError(err?.message || "Something went wrong. Please try again.");
  }

  async function sendCode() {
    setBusy(true);
    setError(null);
    try {
      const result = await sendReactivationCode(token);
      setSent(result);
      setCode("");
      setSecondsLeft(result.resendAfterSeconds);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await reactivateAccount(token, code);
      await onReactivated(session);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  }

  const deletedOn = formatDay(deletedAt);

  return (
    <div>
      {/* The sign-in card's own heading, so both read as one page. */}
      <div className="mb-8 text-center lg:text-left">
        <p className="mb-3 text-center text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--color-accent)] sm:text-xs">
          Welcome back
        </p>
        <h2 className="text-[clamp(2.25rem,8vw,2.875rem)] font-bold leading-tight tracking-[-0.045em] text-[var(--color-text)]">
          Reactivate your account
        </h2>
        <p className="mt-2 text-sm leading-6 text-[var(--color-muted)] sm:text-[15px]">
          {deletedOn
            ? `You deleted this account on ${deletedOn}.`
            : "You deleted this account."}{" "}
          Reactivate it to sign in again; everything is as you left it.
        </p>
      </div>

      {ended ? (
        <div className="space-y-4">
          <p
            role="alert"
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4 text-sm leading-6 text-[var(--color-text)]"
          >
            {ended}
          </p>
          <button type="button" onClick={onCancel} className={PRIMARY_CLASS}>
            Back to sign in
          </button>
        </div>
      ) : null}

      {!ended && !sent ? (
        <div className="space-y-4">
          <p className="text-sm leading-6 text-[var(--color-text)]">
            We will email a 6-digit code to <strong>{email}</strong> to make
            sure it is you.
          </p>
          {error ? (
            <p role="alert" className="text-sm font-medium text-red-700">
              {error}
            </p>
          ) : null}
          <button
            type="button"
            onClick={sendCode}
            disabled={busy}
            className={PRIMARY_CLASS}
          >
            {busy ? "Sending..." : "Email me a code"}
          </button>
        </div>
      ) : null}

      {!ended && sent ? (
        <form onSubmit={submitCode} className="space-y-4">
          <p className="text-sm leading-6 text-[var(--color-text)]">
            We emailed a 6-digit code to <strong>{sent.sentTo}</strong>. It
            works for {Math.round(sent.expiresInSeconds / 60)} minutes.
          </p>
          <div>
            <label
              htmlFor="reactivation-code"
              className="mb-2 block text-[13px] font-bold text-[var(--color-text)]"
            >
              6-digit code
            </label>
            <input
              id="reactivation-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "reactivation-error" : undefined}
              className={INPUT_CLASS}
            />
            {error ? (
              <p
                id="reactivation-error"
                role="alert"
                className="mt-2 text-sm font-medium text-red-700"
              >
                {error}
              </p>
            ) : null}
          </div>
          <button
            type="submit"
            disabled={busy || code.length !== 6}
            className={PRIMARY_CLASS}
          >
            {busy ? "Reactivating..." : "Reactivate account"}
          </button>
          <div className="text-center">
            <button
              type="button"
              onClick={sendCode}
              disabled={busy || secondsLeft > 0}
              className={LINK_CLASS}
            >
              {secondsLeft > 0
                ? `Send a new code in ${secondsLeft}s`
                : "Send a new code"}
            </button>
          </div>
        </form>
      ) : null}

      {!ended ? (
        <div className="mt-6 text-center">
          <button type="button" onClick={onCancel} className={LINK_CLASS}>
            Back to sign in
          </button>
        </div>
      ) : null}
    </div>
  );
}
